/**
 * BankReconciliationVoucher — Printable reconciliation statement.
 *
 * Rendered inside MasterReportPreview (which handles the print modal,
 * @media print isolation, and the Print button).
 */

import type { ReactNode } from 'react';
import type { BankReconciliationDto, BankReconciliationLineDto } from '@/types/bankReconciliation.types';
import { PrintableTable } from '@/components/master/report/PrintableTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '150px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:     { bg: '#fef3c7', text: '#92400e' },
    Completed: { bg: '#d1fae5', text: '#065f46' },
  };
  const c = colour[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

const SummaryRow = ({
  label, value, highlight, currency,
}: { label: string; value: number; highlight?: boolean; currency: string }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    padding: highlight ? '6px 8px' : '3px 8px',
    background: highlight ? '#1e3a5f' : 'transparent',
    borderRadius: highlight ? '4px' : undefined,
    fontSize: highlight ? '11px' : '10px',
    fontWeight: highlight ? 700 : 400,
    color: highlight ? 'white' : '#374151',
    marginTop: highlight ? '4px' : undefined,
  }}>
    <span>{label} ({currency}):</span>
    <span style={{ fontFamily: 'monospace' }}>{fmtNum(value)}</span>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

interface BankReconciliationVoucherProps {
  reconciliation: BankReconciliationDto;
  companyName:    string;
  printedBy?:     string;
}

export const BankReconciliationVoucher = ({
  reconciliation: r,
  companyName,
  printedBy,
}: BankReconciliationVoucherProps): ReactNode => {

  const clearedLines   = r.lines.filter(l => l.isCleared);
  const unclearedLines = r.lines.filter(l => !l.isCleared);

  const lineColumns = [
    { key: 'lineNumber', label: '#', width: '28px', align: 'center' as const,
      render: (l: BankReconciliationLineDto) => l.lineNumber },
    { key: 'transactionNumber', label: 'Ref No.',       width: '80px',
      render: (l: BankReconciliationLineDto) => l.transactionNumber },
    { key: 'transactionDate', label: 'Date',            width: '70px',
      render: (l: BankReconciliationLineDto) => fmtDate(l.transactionDate) },
    { key: 'transactionType', label: 'Type',            width: '70px',
      render: (l: BankReconciliationLineDto) => l.transactionType },
    { key: 'description', label: 'Description',
      render: (l: BankReconciliationLineDto) => l.description || '' },
    { key: 'amount', label: 'Amount', width: '100px', align: 'right' as const,
      render: (l: BankReconciliationLineDto) => (
        <span style={{ color: l.amount >= 0 ? '#065f46' : '#991b1b', fontWeight: 500 }}>
          {fmtNum(l.amount)}
        </span>
      ) },
    { key: 'clearedDate', label: 'Cleared Date',        width: '80px',
      render: (l: BankReconciliationLineDto) => fmtDate(l.clearedDate) },
  ];

  const unclearedColumns = lineColumns.filter(c => c.key !== 'clearedDate');

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: '2px solid #1e3a5f',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
              myLedger ONE
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.2 }}>
              Bank Reconciliation Statement
            </div>
            <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
              {companyName}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
              {r.reconciliationNumber}
            </div>
            <div style={{ marginTop: '4px' }}>
              <StatusBadge status={r.status} />
            </div>
            <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
              Printed: {new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {printedBy && ` · ${printedBy}`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Document details ────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Bank Account
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{r.bankAccountName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace', marginBottom: '4px' }}>{r.bankAccountCode}</div>
          <LabelValue label="Statement Date"  value={fmtDate(r.statementDate)} />
          <LabelValue label="Currency"        value={r.currencyCode} />
          <LabelValue label="Period"          value={`${r.financialPeriodCode} / ${r.financialYearCode}`} />
        </div>
        <div>
          <LabelValue label="Statement Opening" value={fmtNum(r.statementOpeningBalance)} />
          <LabelValue label="Statement Closing" value={fmtNum(r.statementClosingBalance)} />
          <LabelValue label="Book Opening"      value={fmtNum(r.bookOpeningBalance)} />
          <LabelValue label="Book Closing"      value={fmtNum(r.bookClosingBalance)} />
          <LabelValue label="Difference"        value={
            <span style={{ color: r.difference === 0 ? '#065f46' : '#991b1b', fontWeight: 700 }}>
              {fmtNum(r.difference)}
              {r.difference === 0 && ' ✓ Balanced'}
            </span>
          } />
          {r.completedDate && (
            <LabelValue label="Completed Date" value={fmtDate(r.completedDate)} />
          )}
        </div>
      </div>

      {/* Notes */}
      {r.notes && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Notes: </span>
          {r.notes}
        </div>
      )}

      {/* ── Reconciliation Summary ────────────────────────────────────────────── */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Reconciliation Summary
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0 32px',
          padding: '12px',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
        }}>
          {/* Bank Side */}
          <div>
            <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
              Bank Statement Side
            </div>
            <SummaryRow label="Statement Closing Balance" value={r.statementClosingBalance} currency={r.currencyCode} />
          </div>
          {/* Book Side */}
          <div>
            <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
              Book Balance Side
            </div>
            <SummaryRow label="Book Opening Balance"   value={r.bookOpeningBalance}   currency={r.currencyCode} />
            <SummaryRow label="(+) Cleared Credits"    value={r.clearedCredits}        currency={r.currencyCode} />
            <SummaryRow label="(-) Cleared Debits"     value={r.clearedDebits}         currency={r.currencyCode} />
            <SummaryRow label="Book Closing Balance"   value={r.bookClosingBalance}    currency={r.currencyCode} highlight />
          </div>
        </div>

        {/* Difference */}
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          background: r.difference === 0 ? '#d1fae5' : '#fee2e2',
          border: `1px solid ${r.difference === 0 ? '#6ee7b7' : '#fca5a5'}`,
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          fontWeight: 700,
          color: r.difference === 0 ? '#065f46' : '#991b1b',
        }}>
          <span>Difference (Statement − Book) ({r.currencyCode}):</span>
          <span style={{ fontFamily: 'monospace' }}>
            {fmtNum(r.difference)}{r.difference === 0 ? ' — Balanced ✓' : ''}
          </span>
        </div>

        {/* Uncleared summary */}
        <div style={{
          marginTop: '4px',
          padding: '6px 12px',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          fontSize: '10px',
          color: '#6b7280',
          gap: '0 32px',
        }}>
          <span>Uncleared Credits: <strong style={{ color: '#065f46' }}>{fmtNum(r.unclearedCredits)}</strong></span>
          <span>Uncleared Debits: <strong style={{ color: '#991b1b' }}>{fmtNum(r.unclearedDebits)}</strong></span>
        </div>
      </div>

      {/* ── Cleared Items ──────────────────────────────────────────────────────── */}
      {clearedLines.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Cleared Items ({clearedLines.length})
          </div>
          <PrintableTable
            columns={lineColumns}
            rows={clearedLines}
            rowKey={(l) => l.id}
          />
        </div>
      )}

      {/* ── Outstanding / Uncleared Items ─────────────────────────────────────── */}
      {unclearedLines.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Outstanding Items ({unclearedLines.length})
          </div>
          <PrintableTable
            columns={unclearedColumns}
            rows={unclearedLines}
            rowKey={(l) => l.id}
          />
        </div>
      )}

      {/* ── Completion Audit ─────────────────────────────────────────────────────── */}
      {r.status === 'Completed' && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Completion Details
          </div>
          <div style={{
            padding: '8px 12px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
          }}>
            {r.completedDate && (
              <LabelValue label="Completed Date" value={fmtDate(r.completedDate)} />
            )}
            {r.reopenedDate && (
              <>
                <LabelValue label="Reopened Date"   value={fmtDate(r.reopenedDate)} />
                {r.reopenReason && (
                  <LabelValue label="Reopen Reason" value={r.reopenReason} />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Signature blocks ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {['Prepared By', 'Reviewed By', 'Approved By'].map((label) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
